import * as React from "react"
import { Link } from "react-router-dom"

import { cn } from "@/lib/utils"

/*
  Top-bar breadcrumbs — "Supply / Inventory / Catalog", "Projects / LIV Waterside / Stages".

  Layout renders them (desktop top bar) from the route: section + page. A page adds what only it knows —
  the open tab, the project name — with the hook:

    useBreadcrumbs([project?.name, 'Stages'])      // falsy items are skipped; cleared when the page unmounts

  Items are strings or { label, to } (a link). The last item is the current page (aria-current).
*/
const BreadcrumbSetterContext = React.createContext(null)

export function BreadcrumbProvider({ onChange, children }) {
  return <BreadcrumbSetterContext.Provider value={onChange}>{children}</BreadcrumbSetterContext.Provider>
}

export function useBreadcrumbs(items) {
  const setTrail = React.useContext(BreadcrumbSetterContext)
  const key = JSON.stringify((items || []).filter(Boolean))
  React.useEffect(() => {
    if (!setTrail) return undefined
    setTrail(JSON.parse(key))
    return () => setTrail(null)
  }, [setTrail, key])
}

export function Breadcrumbs({ items, className }) {
  const visible = (items || []).filter((item) => item && (typeof item === "string" || item.label))
  if (!visible.length) return null
  return (
    <nav aria-label="Breadcrumb" className={cn("min-w-0", className)} data-testid="app-breadcrumbs">
      <ol className="flex min-w-0 items-center gap-1.5 text-[13px] text-ink-3">
        {visible.map((item, index) => {
          const label = typeof item === "string" ? item : item.label
          const last = index === visible.length - 1
          return (
            <li key={`${index}-${label}`} className={cn("flex min-w-0 items-center gap-1.5", last ? "min-w-0" : "shrink-0")}>
              {index > 0 ? <span className="text-ink-4" aria-hidden="true">/</span> : null}
              {last ? (
                <span className="truncate font-semibold text-ink" aria-current="page">{label}</span>
              ) : typeof item !== "string" && item.to ? (
                <Link to={item.to} className="truncate rounded hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {label}
                </Link>
              ) : (
                <span className="truncate">{label}</span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
